import { useEffect, useRef } from 'react'

/**
 * Shared retina-aware canvas + rAF harness for speech visualizers. Handles DPR
 * scaling, ResizeObserver, the animation loop, and a static single-frame render
 * for reduced-motion. The style only supplies a `draw` function.
 *
 * @param {object}   opts
 * @param {() => number} opts.read       returns the current amplitude (0..1)
 * @param {import('react').MutableRefObject<boolean>} opts.activeRef  is speaking
 * @param {boolean}  opts.reduceMotion   render one static frame, no loop
 * @param {(ctx: CanvasRenderingContext2D, frame: {w:number,h:number,amp:number,active:boolean,t:number}) => void} opts.draw
 * @returns {import('react').RefObject<HTMLCanvasElement>}
 */
export function useVizCanvas({ read, activeRef, reduceMotion, draw }) {
  const canvasRef = useRef(null)
  const rafRef = useRef(null)
  const drawRef = useRef(draw)
  const readRef = useRef(read)

  // Keep the loop-read callbacks current without touching refs during render.
  useEffect(() => {
    drawRef.current = draw
    readRef.current = read
  }, [draw, read])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let cssW = 0
    let cssH = 0
    let frame = 0

    const resize = () => {
      const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
      const rect = canvas.getBoundingClientRect()
      cssW = rect.width
      cssH = rect.height
      canvas.width = Math.max(1, Math.round(cssW * dpr))
      canvas.height = Math.max(1, Math.round(cssH * dpr))
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null
    ro?.observe(canvas)

    const renderOnce = (amp) => {
      ctx.clearRect(0, 0, cssW, cssH)
      drawRef.current(ctx, { w: cssW, h: cssH, amp, active: activeRef.current, t: frame })
    }

    if (reduceMotion) {
      // Static frame at rest.
      renderOnce(0)
    } else {
      const tick = () => {
        frame += 1
        renderOnce(readRef.current())
        rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)
    }

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      ro?.disconnect()
    }
  }, [reduceMotion, activeRef])

  return canvasRef
}
